import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Card from "../components/Card";
import Videos from "../components/Videos";
import Clients from "../components/Clients";
import AdvertisingHeader from "../components/AdvertisingHeader";
import SEO from "../components/SEO";
import {
  ClientSection,
  FAQSection,
} from "../components/BottomSection";
import axios from "../lib/axios";
import { useLocation } from "react-router-dom";
import { FaArrowDown } from "react-icons/fa";
import CelebrityShoutouts from "../components/CelebrityShoutouts";

const designFaqs = [
  {
    question: "What is brand endorsement?",
    answer:
      "Brand endorsement is a marketing strategy where a public figure, celebrity, or influencer promotes your brand, product, or service. Their association with your brand helps build credibility, trust, and awareness among your target audience.",
  },
  {
    question: "Why is brand endorsement important for my business?",
    answer:
      "Brand endorsement helps establish a strong connection with your audience by leveraging the reputation and influence of endorsers. It increases brand visibility, builds consumer trust, and can significantly impact purchase decisions.",
  },
  {
    question: "How do I start a brand endorsement campaign with you?",
    answer:
      "Contact us with your campaign goals, target audience, and budget. We'll propose a strategy, suggest suitable endorsers, and manage the campaign from start to finish.",
  },
];

const BrandEndorsement = () => {
  const location = useLocation();
  const { highlightCard, scrollToCard } = location.state || {};
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("popularity");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    category: [],
    platform: [],
    priceRange: [],
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 12,
  });
  const [hasMore, setHasMore] = useState(true);

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          "https://admin.indorinama.in/api/categories",
        );
        // Filter categories for brand endorsements only
        const brandCategories = response.data.docs.filter(
          (cat) => cat.for === "brand",
        );
        setCategories(brandCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (scrollToCard && highlightCard) {
      console.log({ scrollToCard, highlightCard });
      const element = document.getElementById(`design-${highlightCard}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        element.classList.add("highlight-card");
        // Remove highlight after 2 seconds
        setTimeout(() => {
          element.classList.remove("highlight-card");
        }, 2000);
      }
    }
  }, [highlightCard, scrollToCard, location]);

  useEffect(() => {
    const fetchDesigns = async () => {
      setLoading(true);
      setError(null);

      try {
        // Determine sort parameter based on sortBy value
        let sortParam = "-createdAt";
        switch (sortBy) {
          case "price_low":
            sortParam = "+price";
            break;
          case "price_high":
            sortParam = "-price";
            break;
          case "name":
            sortParam = "name";
            break;
          case "popularity":
          default:
            sortParam = "-createdAt";
            break;
        }

        // Build query params from filters
        const filterParams = new URLSearchParams();
        filterParams.append("page", pagination.page);
        filterParams.append("limit", pagination.limit);
        filterParams.append("sort", sortParam);

        // Add category filters - use category IDs from the API
        if (selectedFilters.category?.length > 0) {
          const categoryIds = selectedFilters.category
            .map((catName) => categories.find((c) => c.name === catName)?.id)
            .filter(Boolean);
          if (categoryIds.length > 0) {
            categoryIds.forEach((id) => {
              filterParams.append("category", id);
            });
          }
        }

        // Add platform filters
        if (selectedFilters.platform?.length > 0) {
          selectedFilters.platform.forEach((platform) => {
            filterParams.append("platform", platform.toLowerCase());
          });
        }

        // Add price range filter
        if (selectedFilters.priceRange?.length > 0) {
          const priceRangeMap = {
            under50K: { min: 0, max: 100000 },
            "50K-1L": { min: 100000, max: 500000 },
            "1L-5L": { min: 500000, max: 1000000 },
            "5L-25L": { min: 1000000, max: null },
          };
          const selectedRange = priceRangeMap[selectedFilters.priceRange[0]];

          if (selectedRange) {
            filterParams.append("minimum_billing_lte", selectedRange.min);
            if (selectedRange.max) {
              filterParams.append("minimum_billing_gte", selectedRange.max);
            }
          }
        }

        const url = `/api/brand-endorsements/search?${filterParams.toString()}`;
        const response = await axios.get(url);
        const data = response.data;

        if (response.status === 200) {
          const results = Array.isArray(data.docs) ? data.docs : [];

          if (pagination.page === 1) {
            setDesigns(results);
          } else {
            setDesigns((prev) => [...prev, ...results]);
          }

          if (data) {
            setPagination((prev) => ({
              ...prev,
              total: data.totalDocs,
              pages: data.totalPages,
            }));
            setHasMore(pagination.page < data.totalPages);
          }
        } else {
          setError(data.message || "Failed to fetch designs");
        }
      } catch (error) {
        setError("Failed to fetch design services");
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, [pagination.page, pagination.limit, selectedFilters, sortBy, categories]);

  const filteredAndSortedDesigns = useMemo(() => {
    return designs;
  }, [designs]);

  const sortOptions = [
    { value: "popularity", label: "Sort by Popularity" },
    { value: "name", label: "Sort by Name" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
  ];

  const handleSortClick = (value) => {
    setSortBy(value);
    setShowSortDropdown(false);
  };

  const handleFilterChange = (filterType, values) => {
    let newFilters;
    if (filterType === "priceRange") {
      // Handle price range filter
      if (selectedFilters.priceRange?.includes(values)) {
        // Deselect if already selected
        newFilters = {
          ...selectedFilters,
          priceRange: [],
        };
      } else {
        // Select new price range
        newFilters = {
          ...selectedFilters,
          priceRange: [values],
        };
      }
    } else {
      // Handle other filter types (category, platform)
      newFilters = {
        ...selectedFilters,
        [filterType]: values,
      };
    }

    setSelectedFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setDesigns([]); // Clear existing designs when filters change
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Brand Endorsement"
        description="Get professional brand endorsements from celebrities and famous personalities. Find the perfect brand ambassador for your business at Indorinama."
        keywords="brand endorsement, celebrity endorsement, brand ambassador, celebrity brand collaboration"
        url="https://indorinama.com/brand-endorsement"
      />
    <div className="brand-endorsement">
      <Navbar />
      <CelebrityShoutouts />
      <div className="digital-section">
        <div className="digital">
          <div className="md:max-w-[80vw] mx-auto">
            <AdvertisingHeader
              category="brand-endorsement"
              sortOptions={sortOptions}
              showFilterOptions={true}
              sortBy={sortBy}
              handleSortClick={handleSortClick}
              showSortDropdown={showSortDropdown}
              setShowSortDropdown={setShowSortDropdown}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              filterOptions={{
                category: categories.map((cat) => cat.name),
                platform: [
                  "Instagram",
                  "LinkedIn",
                  "Facebook",
                  "YouTube",
                  "Twitter",
                ],
              }}
            />
            <p className="text-gray-600 px-4 -mt-6">
              Showing {filteredAndSortedDesigns.length} of {pagination.total}{" "}
              results
            </p>
          </div>
        </div>
      </div>

      {/* filter seciton */}

      <section className="py-8 mb-6">
        <div className="md:max-w-[80vw] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-4">
            <img src="/title.svg" alt="title" className="w-full h-16" />
          </div>
          <div className="flex justify-center">
            <div className="flex flex-wrap gap-6 justify-center">
              <button
                onClick={() => handleFilterChange("priceRange", "under50K")}
                className="relative hover:scale-105 transition-all duration-300 bg-[#DC2626] z-[49] h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
              >
                <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                  <span className="-ml-2 -mt-2">Under ₹1L</span>
                </div>
              </button>
              <button
                onClick={() => handleFilterChange("priceRange", "50K-1L")}
                className="relative hover:scale-105 transition-all duration-300 bg-[#B31F1F] z-20 h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
              >
                <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                  <span className="-ml-2 -mt-2">₹1L - ₹5L</span>
                </div>
              </button>
              <button
                onClick={() => handleFilterChange("priceRange", "1L-5L")}
                className="relative hover:scale-105 transition-all duration-300 bg-[#8D1818] z-10 h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
              >
                <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                  <span className="-ml-2 -mt-2">₹5L - ₹10L</span>
                </div>
              </button>
              <button
                onClick={() => handleFilterChange("priceRange", "5L-25L")}
                className="relative hover:scale-105 transition-all duration-300 bg-[#681212] z-0 h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
              >
                <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                  <span className="-ml-2 -mt-2">Above ₹10L</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-4">
        <div className="md:max-w-[80vw] mx-auto px-4 sm:px-6 lg:px-8">
          {designs.length === 0 && !loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No designs found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredAndSortedDesigns.map((design, index) => {
                if (filteredAndSortedDesigns.length === index + 1) {
                  return (
                    <div key={design._id}>
                      <Card
                        data={design}
                        category="brand-endorsement"
                        options={true}
                      />
                    </div>
                  );
                } else {
                  return (
                    <Card
                      key={design._id}
                      data={design}
                      category="brand-endorsement"
                      options={true}
                    />
                  );
                }
              })}
            </div>
          )}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                className="flex items-center px-6 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
              >
                Load More
                <FaArrowDown className="ml-2" />
              </button>
            </div>
          )}
          {loading && (
            <div className="flex justify-center mt-8">
              <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      </section>

      {/* <div className="flex justify-center items-center">
        <img src={img1} alt="achievements" />
      </div> */}
      {/* Floating Stats */}
      <div className="mx-auto max-w-[80vw] grid gap-4 my-10">
        <img src="/celebrity.svg" className="w-full h-auto" alt="Celebrity shoutouts" />
      </div>
      <Clients category="brand-endorsement" />
      <Videos category="brand-endorsement" />
      <FAQSection pageFAQ={designFaqs} />
      <div className="-mt-16 -mb-6">
        <ClientSection key={"brand_endorsement_client_logos"} />
      </div>
      <Footer />
    </div>
    </>
  );
};

export default BrandEndorsement;
